import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import Menu from '../../panel/Menu';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Divider
} from "@mui/material";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .page-fade-in {
    animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* --- METICULOUS PRINT STYLES --- */
  @media print {
    @page {
        size: A4;
        margin: 5mm;
    }

    html, body, #root {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        color: #000 !important;
    }

    .no-print, nav, header, footer, .sidebar, button, .MuiTextField-root {
        display: none !important;
    }

    * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
        text-shadow: none !important;
        animation: none !important;
        border-color: #ccc !important;
    }

    .page-fade-in {
        padding: 0 !important;
        margin: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
    }

    .print-area {
        border: none !important;
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
    }

    table {
        width: 100% !important;
        border-collapse: collapse !important;
    }
    
    th, td {
        border: 1px solid #ddd !important;
        padding: 6px 8px !important;
        color: #000 !important;
    }

    th {
        background-color: #f5f5f5 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        font-weight: bold !important;
    }
  }
`;

function Roomtypewiseonline() {

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        background: colors.calPolyGreen,
        color: colors.vanilla
    });

    const printRef = useRef();

    const [loading, setLoading] = useState(false);
    
    // Default values set to today's date
    const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    
    const [bookingData, setBookingData] = useState([]);
    const [propertyId, setPropertyId] = useState(null);
    const [userData, setUserData] = useState(null);

    /* 🔐 AUTH + PROPERTY */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            setUserData(user);

            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (roleSnap.exists()) {
                setPropertyId(roleSnap.data().propertyId);
            } else {
                Toast.fire({ icon: "error", title: "Property not assigned" });
            }
        });

        return () => unsub();
    }, []);

    // Auto-trigger report when propertyId is ready
    useEffect(() => {
        if (propertyId && fromDate && toDate) {
            getReport();
        }
    }, [propertyId]);

    /* 📊 GET REPORT */
    const getReport = async () => {
        if (!fromDate || !toDate) {
            Toast.fire({ icon: "error", title: "Select From & To Date" });
            return;
        }

        if (!propertyId) {
            Toast.fire({ icon: "error", title: "Property not assigned" });
            return;
        }

        setLoading(true);
        setBookingData([]);

        try {
            /* ROOM TYPES */
            const roomTypeSnap = await getDocs(
                query(
                    collection(db, "rooms"),
                    where("propertyId", "==", propertyId)
                )
            );

            const roomTypes = roomTypeSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));

            if (!roomTypes.length) {
                Toast.fire({ icon: "info", title: "No Room Types Found" });
                setLoading(false);
                return;
            }

            const rTypeIds = roomTypes.map(r => r.id).slice(0, 30);

            /* WEBSITE BOOKINGS */
            const bookingSnap = await getDocs(
                query(
                    collection(db, "websitebookings"),
                    where("propertyId", "==", propertyId),
                    where("rtypeid", "in", rTypeIds),
                    where("status", "==", "Paid"),
                    where("checkindate", ">=", fromDate),
                    where("checkindate", "<=", toDate)
                )
            );

            const bookings = bookingSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));

            /* GROUP BY ROOM TYPE */
            const finalData = roomTypes.map(rt => ({
                roomtype: rt.roomtype,
                bookings: bookings.filter(b => b.rtypeid === rt.id)
            }));

            setBookingData(finalData);
        } catch (err) {
            console.error(err);
            Toast.fire({ icon: "error", title: "Error loading report" });
        } finally {
            setLoading(false);
        }
    };

    /* 🖨 PRINT */
    const handlePrint = () => {
        window.print();
    };

    const handleFromDate = (e) => {
        setFromDate(e.target.value);
    };

    const handleToDate = (e) => {
        setToDate(e.target.value);
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }} className="page-fade-in">
            <style>{animationStyles}</style>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>

                <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }} className="no-print">
                    Room Type Wise Website Bookings
                </Typography>

                {/* FILTER */}
                <Card sx={{ mb: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }} className="no-print">
                    <CardContent sx={{ p: 3 }}>
                        <div className="container-fluid p-0">
                            <div className="row align-items-center">
                                <div className="col-sm-4 mb-3 mb-sm-0">
                                    <TextField
                                        type="date"
                                        label="From Date"
                                        value={fromDate}
                                        onChange={handleFromDate}
                                        fullWidth size="small"
                                        InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-4 mb-3 mb-sm-0">
                                    <TextField
                                        type="date"
                                        label="To Date"
                                        value={toDate}
                                        onChange={handleToDate}
                                        fullWidth size="small"
                                        InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-4">
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        disabled={loading}
                                        onClick={getReport}
                                        sx={{ 
                                            bgcolor: colors.goldenrod, 
                                            color: colors.darkGreen, 
                                            fontWeight: 700, 
                                            textTransform: "none", 
                                            borderRadius: "8px",
                                            height: "40px",
                                            '&:hover': { bgcolor: colors.gold } 
                                        }}
                                    >
                                        {loading ? "Loading..." : "Get Report"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* REPORT */}
                <Card className="print-area" ref={printRef} sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardContent sx={{ p: 3 }}>

                        <Box textAlign="center" mb={3}>
                            <Typography fontWeight="bold" sx={{ color: colors.gold }}>
                                From {fromDate ? moment(fromDate).format("DD/MM/YYYY") : "—"}
                                {" "}to{" "}
                                {toDate ? moment(toDate).format("DD/MM/YYYY") : "—"}
                            </Typography>
                        </Box>

                        {bookingData.map((rt, idx) => (
                            <Box key={idx} mb={4}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{ background: colors.darkGreen, color: colors.gold, p: 1.5, borderRadius: "8px 8px 0 0", borderTop: `1px solid ${colors.goldenrod}`, borderX: `1px solid ${colors.goldenrod}` }}
                                >
                                    {rt.roomtype}
                                </Typography>

                                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "0 0 8px 8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: colors.darkGreen }}>
                                            <TableRow>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Sr</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Name</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Mobile</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Checkin</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Checkout</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Rooms</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Adults</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Plan</TableCell>
                                                <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                                                <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Discount</TableCell>
                                                <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Paid</TableCell>
                                                <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Remaining</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {rt.bookings.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={12} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                                                        No bookings found for this room type
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {rt.bookings.map((b, i) => (
                                                <TableRow key={i} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{i + 1}</TableCell>
                                                    <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.name}</TableCell>
                                                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.phone}</TableCell>
                                                    <TableCell sx={{ color: colors.vanilla, opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{moment(b.checkindate).format("DD/MM/YYYY")}</TableCell>
                                                    <TableCell sx={{ color: colors.vanilla, opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{moment(b.checkoutdate).format("DD/MM/YYYY")}</TableCell>
                                                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.noofroom}</TableCell>
                                                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.adult}</TableCell>
                                                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.roomtype} / {b.planname}</TableCell>
                                                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.total}</TableCell>
                                                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.discountamount}</TableCell>
                                                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.advance}</TableCell>
                                                    <TableCell align="right" sx={{ color: colors.gold, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.remaining}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Divider sx={{ mt: 3, borderColor: colors.goldenrod, opacity: 0.2 }} />
                            </Box>
                        ))}

                    </CardContent>
                </Card>

                <Box textAlign="center" mt={3} className="no-print">
                    <Button 
                        variant="contained" 
                        onClick={handlePrint}
                        sx={{ 
                            bgcolor: colors.goldenrod, 
                            color: colors.darkGreen, 
                            fontWeight: 700, 
                            textTransform: "none", 
                            borderRadius: "8px",
                            px: 4,
                            py: 1.2,
                            '&:hover': { bgcolor: colors.gold } 
                        }}
                    >
                        Print Report
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default Roomtypewiseonline;